import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { MethodEditorProps } from '../types';
import { FORM_FIELD_EXAMPLES } from '../constants';

export const SetDefaultPictureEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const normalizedPayload = {
    form: 'app/CampaignPageForm',
    categories: [],
    ...payload,
  };

  const addCategory = () => {
    onChange({
      ...normalizedPayload,
      categories: [...normalizedPayload.categories, ''],
    });
  };

  const updateCategory = (index: number, value: string) => {
    const newCategories = [...normalizedPayload.categories];
    newCategories[index] = value;
    onChange({
      ...normalizedPayload,
      categories: newCategories,
    });
  };

  const removeCategory = (index: number) => {
    const newCategories = normalizedPayload.categories.filter(
      (_: string, i: number) => i !== index
    );
    onChange({
      ...normalizedPayload,
      categories: newCategories,
    });
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel id="form-select-label">Form</InputLabel>
        <Select
          labelId="form-select-label"
          label="Form"
          value={normalizedPayload.form}
          onChange={(e) => onChange({ ...normalizedPayload, form: e.target.value })}
        >
          {FORM_FIELD_EXAMPLES.map((form) => (
            <MenuItem key={form} value={form}>
              {form}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2">Picture Categories</Typography>
        <Button onClick={addCategory} startIcon={<AddIcon />} variant="outlined" size="small">
          Add Category
        </Button>
      </Box>

      {normalizedPayload.categories.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            No categories specified. When no categories are provided, a random picture will be
            selected from all available pictures.
          </Typography>
        </Alert>
      )}

      {normalizedPayload.categories.map((category: string, index: number) => (
        <Box key={category} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={category}
            onChange={(e) => updateCategory(index, e.target.value)}
            placeholder="Enter category name"
            label={`Category ${index + 1}`}
          />
          <IconButton onClick={() => removeCategory(index)} color="error" size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>How it works:</strong> This method selects a random picture that matches at least
          one of the specified categories.
          <br />
          <strong>Form Field:</strong> The selected picture URL will be set in the
          &ldquo;pictures&rdquo; field of the specified form.
          <br />
          <strong>Category Matching:</strong> Pictures with categories that overlap with your
          specified categories will be prioritized.
          <br />
          <strong>Fallback:</strong> If no pictures match the categories, a random picture from all
          available pictures will be selected.
          <br />
          <strong>Prerequisites:</strong> Pictures must be loaded into context using the &ldquo;Load
          Pictures&rdquo; method before using this action.
        </Typography>
      </Alert>
    </Box>
  );
};
